import { getContext, hasContext, setContext } from "svelte";

const WORKFLOW = Symbol("WORKFLOW")
const STEP = Symbol("STEP")
const ASIDE = Symbol("ASIDE")

class Workflow {
  #disabled = $state<boolean>(false)
  #steps = $state<Array<Step|Workflow>>([])
  #bookmark : number = $state(-1) // Start from no step selected, choose the first not-disabled one on initial mount in currentStep
  #asides = new Map<string, Aside>()
  #asideName : string | null = $state(null)
  #closed : boolean = false
  #parent : Workflow | null
  #index : number | null = null
  #baseState = $state<WorkflowState>({})

  constructor(parent: Workflow | null = null) {
    this.#parent = parent
    this.#index = parent?._addStep(this) ?? null
  }

  // WARNING: If first n step(s) are disabled, currentStep returns the first enabled step without updating
  // #bookmark, which means that currentStep can surprisingly regress if disabled status of an earlier step
  // changes before #bookmark gets set by (e.g.) nextStep.
  get currentStep(): Step | Workflow | null {
    let step;
    if(step = this.#steps[this.#bookmark]) return step;
    for(let i = this.#bookmark + 1; i < this.#steps.length; i++) {
      step = this.#steps[i];
      if(!step.disabled) return step;
    }
    return null;
  }

  get disabled() { return this.#disabled }
  set disabled(v) { this.#disabled = v }
  get index() { return this.#index }
  get path() { return null } // Workflow doesn't have a path, but needs to respond to this 

  get currentAside(): Aside | null {
    return this.#asideName && this.#asides.get(this.#asideName) || null;
  }

  get root(): Workflow {
    return this.#parent?.root || this
  }

  get active() {
    return !this.#parent || this.#parent.currentStep == this
  }

  get length() {
    return this.#steps.filter((s)=>!s.disabled).length
  }

  get state() {
    return {...this.#baseState, ...this.currentStep?.state};
  }

  set state(value : WorkflowState) {
    this.#baseState = value;
  }

  restart() {
    for(let i = 0; i < this.#steps.length; i++){
      let step = this.#steps[i];
      if(!step.disabled) {
        this.#bookmark = i;
        return true;
      }
    }
    return false;
  }

  // Depth-first search of own steps for step with matching path, then (if not found already) search up the parent tree
  step(path: string, exclude: Workflow | null = null) {
    for(let i = 0; i < this.#steps.length; i++){
      let step = this.#steps[i];
      if(step.disabled) continue;
      const found = (step instanceof Step && step.path === path) ||
        (step instanceof Workflow && step != exclude && step.step(path, exclude));
      if(found) {
        this.#bookmark = i;
        return true;
      }
    }
    if(this.#parent?.step(path, this)) {
      if(this.#parent.currentStep!.index! > this.index!) {
        this.#bookmark = this.#steps.length - 1
      } else {
        this.#bookmark = 0
      }
      return true;
    }
    return false
  }

  // changeState resolving to exactly false cancels navigation, leaving the current step in place
  async nextStep(changeState: ReturnFalseToCancel = async ()=>{}): Promise<boolean> {
    const current = this.currentStep
    const currentIndex = current ? this.#steps.indexOf(current) : -1;
    const originalBookmark = this.#bookmark;
    this.#bookmark = currentIndex;
    if(await changeState() === false) return false;
    if(current instanceof Step && current.oncomplete) setTimeout(current.oncomplete);
    for(let i = currentIndex + 1; i < this.#steps.length; i++){
      let step = this.#steps[i];
      if(!step.disabled) {
        this.#bookmark = i;
        return true;
      }
    }
    this.#bookmark = originalBookmark;
    if(this.#parent) return this.#parent.nextStep();
    return false;
  }

  async prevStep(changeState: ReturnFalseToCancel = async ()=>{}): Promise<boolean> {
    const current = this.currentStep
    const currentIndex = current ? this.#steps.indexOf(current) : this.#steps.length;
    const originalBookmark = this.#bookmark;
    this.#bookmark = currentIndex;
    if(await changeState() === false) return false;
    for(let i = currentIndex - 1; i >= 0; i--){
      let step = this.#steps[i];
      if(!step.disabled) {
        this.#bookmark = i;
        return true;
      }
    }
    this.#bookmark = originalBookmark;
    if(this.#parent) return this.#parent.prevStep();
    return false;
  }

  aside(name: string | null): void {
    if(name) {
      if(!this.#asides.has(name)) {
        if(this.#parent) return this.#parent.aside(name);
        throw `Cannot activate undefined aside "${name}"`
      }
    } else {
      if(!this.#asideName && this.#parent) return this.#parent.aside(name);
    }
    this.#asideName = name
  }

  close(){
    this.#closed = true
  }

  _addStep(step: Step | Workflow) {
    if(this.#closed) {
      if(step instanceof Workflow) {
        console.error("Don't add or remove descendant <Workflow> components: include them all from the beginning, and use <Workflow disabled> when one doesn't apply")
      } else {
        console.error("Don't add or remove <Step> components: include them all from the beginning, and use <Step disabled> when one doesn't apply")
      }
    }
    this.#steps.push(step)
    return this.#steps.length - 1
  }

  _addAside(name: string, aside: Aside) {
    if(this.#closed) {
      console.error("Don't add or remove <Aside> components: include them all from the beginning.")
    }
    this.#asides.set(name, aside)
  }
}

class Step {
  #disabled = $state<boolean>(false)
  #workflow : Workflow
  #index : number
  #path : string | undefined
  state = $state<WorkflowState>({})
  oncomplete : (()=>void) | null | undefined

  constructor(workflow : Workflow, path? : string) {
    this.#workflow = workflow
    this.#index = workflow._addStep(this)
    this.#path = path
    this.oncomplete = undefined
  }

  get disabled() { return this.#disabled }
  set disabled(v) { this.#disabled = v }
  get index(){ return this.#index }
  get path(){ return this.#path }
  set path(v) { this.#path = v }

  get active(){
    return this.#workflow.currentStep == this
  }

  get hidden(){
    return this.#workflow.currentAside !== null;
  }

  get visible(){
    return this.active && !this.hidden
  }

  next(changeState: ReturnFalseToCancel = async ()=>{}){
    return this.#workflow.nextStep(changeState)
  }

  prev(changeState: ReturnFalseToCancel = async ()=>{}){
    return this.#workflow.prevStep(changeState)
  }
}

class Aside {
  #workflow : Workflow
  #name : string

  constructor(name : string, workflow : Workflow) {
    this.#name = name
    this.#workflow = workflow
    workflow._addAside(name, this)
  }

  get name(){ return this.#name }

  get active(){
    return this.#workflow.currentAside == this
  }
}

type WorkflowState = { [key: string]: any }
type ReturnFalseToCancel = () => unknown

export type { Workflow, Step, Aside, WorkflowState }

export function createWorkflow(){
  const parent = getWorkflow() || null;
  const workflow = new Workflow(parent);
  setContext(WORKFLOW, workflow);
  return workflow;
}

export function getWorkflow(){
  return getContext<Workflow>(WORKFLOW);
}

export function hasWorkflow(){
  return hasContext(WORKFLOW);
}

export function createStep(path : string | undefined){
  if(!hasWorkflow()) throw "Cannot mount <Step> without a parent <Workflow>";
  const step = new Step(getWorkflow(), path)
  setContext(STEP, step)
  return step
}

export function getStep(){
  return getContext<Step>(STEP);
}

export function createAside(name: string){
  if(!hasWorkflow()) throw "Cannot mount <Aside> without a parent <Workflow>";
  const aside = new Aside(name, getWorkflow())
  setContext(ASIDE, aside)
  return aside
}